import React, { FC, ReactElement } from 'react';
import { Link, LinkProps, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { Dictionary } from '../../../../../types/globals';
import { useIsBellaPageType } from '../../../hooks/UrlManager/pageTypeHooks';
import useRouter from '../../../hooks/useRouter';

export const StyledAnchor = styled.a`
    text-decoration: none;
`;

export const StyledLink = styled(Link)`
    text-decoration: none;
`;

export const StyledNavLink = styled(NavLink)`
    text-decoration: none;
`;

export interface Props extends LinkProps {
    to: string;
    className?: string;
    isCurrentPage?: boolean;
    target?: string;
    isNavLink?: boolean;
    activeClassName?: string;
    testId?: string;
    'data-testid'?: string;
}

const BellaLink: React.FC<Props> = ({
    children,
    to,
    className,
    isNavLink,
    activeClassName,
    innerRef,
    ...otherAttributesAndHandlers
}) => {
    return isNavLink ? (
        <StyledNavLink
            to={to}
            activeClassName={activeClassName}
            className={className}
            innerRef={innerRef}
            {...otherAttributesAndHandlers}
        >
            {children}
        </StyledNavLink>
    ) : (
        <StyledLink to={to} className={className} innerRef={innerRef} {...otherAttributesAndHandlers}>
            {children}
        </StyledLink>
    );
};

const buildAdditionalAttributes = ({
    isCurrentPage,
    'aria-current': ariaCurrent,
    target,
    'data-testid': dataTestId,
    testId,
}: Pick<Props, 'isCurrentPage' | 'target' | 'testId' | 'data-testid' | 'aria-current'>): Dictionary<string> => {
    const additionalAttributes: Dictionary<string> = {};
    const notEmptyTestId = dataTestId || testId;

    if (isCurrentPage || ariaCurrent) {
        additionalAttributes['aria-current'] = ariaCurrent?.toString() || 'page';
    }

    if (target) {
        additionalAttributes.target = target;
        if (target === '_blank') {
            additionalAttributes.rel = 'noopener noreferrer';
        }
    }

    if (notEmptyTestId) {
        additionalAttributes['data-testid'] = notEmptyTestId;
    }

    return additionalAttributes;
};

const HybridLink: FC<Props> = ({
    to,
    children,
    className,
    onClick: onClickFunction,
    isCurrentPage = false,
    onMouseEnter: onMouseEnterFunction,
    innerRef,
    target,
    isNavLink,
    activeClassName,
    testId,
    'data-testid': dataTestId,
    'aria-current': ariaCurrent,
}): ReactElement => {
    const additionalAttributes = buildAdditionalAttributes({
        isCurrentPage,
        'aria-current': ariaCurrent,
        target,
        testId,
        'data-testid': dataTestId,
    });
    const { disableClientsideNavigation } = useRouter();
    const isBellaPageType = useIsBellaPageType();
    const { isBellaPage, relativeUrl } = isBellaPageType(to);
    const isExternalLink = !isBellaPage;
    const eventHandlers = { onClick: onClickFunction, onMouseEnter: onMouseEnterFunction };
    const additionalProps = { ...eventHandlers, ...additionalAttributes };

    return (
        <>
            {isExternalLink || !!disableClientsideNavigation ? (
                <StyledAnchor href={(to || '').toString()} className={className} {...additionalProps}>
                    {children}
                </StyledAnchor>
            ) : (
                <BellaLink
                    isNavLink={isNavLink}
                    activeClassName={activeClassName}
                    to={relativeUrl || to}
                    className={className}
                    innerRef={innerRef}
                    {...additionalProps}
                >
                    {children}
                </BellaLink>
            )}
        </>
    );
};

export default HybridLink;
